import { Autocomplete, AutocompleteProps, AutocompleteRenderInputParams, TextField, TextFieldProps } from "@mui/material"
import { Controller, FieldValues, Path, useFormContext, Control } from "react-hook-form"
import { useTranslation } from "react-i18next";

interface FormAutocomplete2Props<IFormValues extends FieldValues = FieldValues, T extends any = any, Multiple extends (boolean | undefined) = undefined, DisableClearable extends (boolean | undefined) = undefined, FreeSolo extends (boolean | undefined) = undefined>
    extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'ref' | 'value' | 'renderInput'> {
    name: Path<IFormValues>,
    control?: Control<IFormValues, any>,
    shouldUnregister?: boolean
    label?: string,
    renderTextFieldProps?: (params: AutocompleteRenderInputParams) => Omit<TextFieldProps, 'name' | 'value' | 'label' | 'error'>
}

const FormAutocomplete2 = <IFormValues extends FieldValues = FieldValues, T extends any = any, Multiple extends boolean | undefined = boolean, DisableClearable extends boolean | undefined = boolean, FreeSolo extends boolean | undefined = boolean>({
    name,
    control,
    shouldUnregister,
    label,
    renderTextFieldProps,
    ...props
}: FormAutocomplete2Props<IFormValues, T, Multiple, DisableClearable, FreeSolo>) => {

    const { t } = useTranslation('booking');

    const formContext = useFormContext<IFormValues>();

    return (
        <Controller
            name={name}
            control={control ?? formContext.control}
            shouldUnregister={shouldUnregister}
            render={({ field, fieldState, formState }) =>
                <Autocomplete
                    {...props}
                    openOnFocus
                    autoHighlight
                    autoSelect
                    ref={field.ref}
                    value={field.value}
                    onChange={(e, value, reason, details) => {
                        field.onChange(value);
                        props.onChange?.(e, value, reason, details);
                    }}
                    onBlur={(e) => {
                        field.onBlur();
                        props.onBlur?.(e);
                    }}
                    disabled={props.disabled ?? formState.isSubmitting}
                    fullWidth
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            {...renderTextFieldProps?.(params)}
                            label={label}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message  ? t(fieldState.error.message) : renderTextFieldProps?.(params).helperText}
                            FormHelperTextProps={{
                                component: "div"
                            } as any}
                            fullWidth
                        />
                    } />
            }
        />
    )
};

export default FormAutocomplete2;